/**
 * ============================================================================
 * Purchase 模块 API 工厂函数
 * ============================================================================
 * 
 * 📋 文件职责：
 * - 定义 Purchase（采购管理）模块的所有 API 请求方法
 * - 通过工厂模式实现请求客户端的依赖注入
 * - 提供完整的 TypeScript 类型安全
 * 
 * 🎯 核心概念：工厂模式
 * - 工厂函数：createPurchaseApi(client) - 接收请求客户端，返回 API 方法集合
 * - 依赖注入：不依赖具体的请求实现（axios、uni.request 等）
 * - 平台无关：Web 端和 App 端都可以使用，只需提供不同的适配器
 * 
 * 💡 设计理念：
 * - "给我一个能发送请求的工具，我就能为你生成所有 API 方法"
 * - 请求客户端是"工具"，API 方法是"产品"，工厂函数是"生产线"
 * 
 * 📖 使用方式：
 * 
 * **Web 端（Vben Admin + axios）**：
 * ```typescript
 * // 1. 创建适配器（让 Vben 的 requestClient 符合 IRequestClient 接口）
 * import { requestClient } from '#/api/request'
 * import type { IRequestClient } from '@bayes-mes/shared/api/core/client'
 * 
 * const webAdapter: IRequestClient = {
 *   get: (url, config) => requestClient.get(url, config).then(res => res.data),
 *   post: (url, data, config) => requestClient.post(url, data, config).then(res => res.data),
 *   put: (url, data, config) => requestClient.put(url, data, config).then(res => res.data),
 *   delete: (url, config) => requestClient.delete(url, config).then(res => res.data),
 * }
 * 
 * // 2. 调用工厂函数，传入适配器
 * import { createPurchaseApi } from '@bayes-mes/shared/api/purchase/factory'
 * export const purchaseApi = createPurchaseApi(webAdapter)
 * 
 * // 3. 使用生成的 API
 * const suppliers = await purchaseApi.getSupplierList({ page: 1 })
 * ```
 * 
 * **App 端（uni-app X）**：
 * ```typescript
 * // 1. 创建适配器（让 uni.request 符合 IRequestClient 接口）
 * const uniAdapter: IRequestClient = {
 *   get: (url, config) => new Promise((resolve, reject) => {
 *     uni.request({
 *       url,
 *       method: 'GET',
 *       data: config?.params,
 *       success: (res) => resolve(res.data.data),
 *       fail: reject
 *     })
 *   }),
 *   // ... 其他方法
 * }
 * 
 * // 2. 调用工厂函数
 * import { createPurchaseApi } from '@bayes-mes/shared/api/purchase/factory'
 * export const purchaseApi = createPurchaseApi(uniAdapter)
 * ```
 * 
 * ⚠️ 注意事项：
 * - **请求客户端接口**：所有工厂函数都使用 `api/core/client.ts` 中定义的 `IRequestClient` 接口
 * - **不要重复定义接口**：从 `../core/client` 导入，不要在每个模块中重新定义
 * - **适配器职责**：适配器负责将平台特定的请求客户端转换为符合 `IRequestClient` 的对象
 * - **响应数据解包**：适配器需要处理响应数据的解包（如 `res.data.data`）
 * - **类型安全**：所有方法都有完整的 TypeScript 类型定义
 * - **注释详细**：每个方法都有详细的中文注释，包括使用场景、参数、返回值、数据流向
 * 
 * 🔄 更新时机：
 * - 后端新增 API 方法
 * - 后端修改参数或响应结构
 * - 后端修改路由路径
 * 
 * ✅ 验证方式：
 * - 确保所有 Controller 的 public 方法都已映射
 * - 确保所有类型导入正确
 * - 确保 URL 路径与后端路由一致（从 routes.ts 引用）
 * - 运行 `npm run type-check` 验证 TypeScript 编译
 * 
 * @module api/purchase/factory
 * @description Purchase（采购管理）模块的 API 工厂函数
 * 
 * @source 对应后端文件:
 * - backend/app/Modules/Purchase/Routes/api.php (路由定义)
 * - backend/app/Modules/Purchase/Controllers/SupplierController.php (供应商控制器)
 * - backend/app/Modules/Purchase/Controllers/SupplierMaterialController.php (供应商物料控制器)
 * - backend/app/Modules/Purchase/Controllers/PurchaseOrderController.php (采购订单控制器)
 * - backend/app/Modules/Purchase/Controllers/ReceivingController.php (收货控制器)
 * - backend/app/Modules/Purchase/Controllers/DashboardController.php (看板控制器)
 * 
 * @generated AI-assisted
 * @author AI Assistant
 * @created 2025-11-04
 * @last-updated 2025-11-04
 * 
 * @change-log
 * - 2025-11-04: 初始创建，实现所有 Controller 方法映射
 * 
 * @notes
 * - API 路径前缀: /api/v1/purchase
 * - 包含5个子资源：供应商、供应商物料、采购订单、收货、看板
 * - 使用工厂模式实现平台无关的 API 调用
 * - 支持完整的 TypeScript 类型推断
 * - IRequestClient 接口定义在 api/core/client.ts
 * - 所有路径从 API_ROUTES.purchase 模块路由常量引用（v3.13.0+ 强制要求）
 * ============================================================================
 */

// ============================================================================
// 类型导入
// ============================================================================

import type {
  // 响应类型
  Supplier,
  SupplierMaterial,
  SupplierRating,
  PurchaseOrder,
  PurchaseOrderItem,
  PurchaseReceiving,
  PurchaseReturn,
  SupplierStatistics,
  
  // 请求类型
  CreateSupplierForm,
  UpdateSupplierForm,
  CreatePurchaseOrderForm,
  UpdatePurchaseOrderForm,
  AddSupplierMaterialForm,
  UpdateSupplierMaterialForm,
  ReceiveMaterialForm,
  RateOrderForm,
  CreateReturnFromReceivingForm,
  
  // 查询参数
  SupplierQueryParams,
  PurchaseOrderQueryParams,
  ReceivingQueryParams,
} from '../../types/purchase'

// 供应商索赔类型
import type {
  SupplierClaim,
  SupplierClaimListParams,
  CreateSupplierClaimParams,
  UpdateSupplierClaimParams,
  SupplierClaimActionParams,
} from '../../types/supplier-claim'

// 供应商绩效类型
import type {
  SupplierPerformance,
  PerformanceDashboard,
  SupplierPerformanceDetail,
  UpdateLevelResult,
  BatchUpdateLevelsResult,
  TopSupplier,
  AlertSupplier,
  MonthlyTrendItem,
} from '../../types/supplier-performance'

import type { PaginationResult, BatchImportResult } from '../../types/api'

// ============================================================================
// 导入统一的请求客户端接口（⚠️ 重要）
// ============================================================================
// 不要重复定义接口！所有工厂函数共享同一个接口
import type { IRequestClient } from '../core/client'

// ============================================================================
// 导入 API 路由定义（⚠️ 必须从 routes.ts 引用）
// ============================================================================
import { API_ROUTES } from '../routes'

// ============================================================================
// Purchase API 工厂函数
// ============================================================================

/**
 * 创建 Purchase 模块的 API 方法集合
 * 
 * @description
 * 这是一个工厂函数，接收一个请求客户端，返回一组 API 方法。
 * 
 * **工厂模式的核心思想**：
 * - 输入：一个能发送 HTTP 请求的客户端（符合 IRequestClient 接口）
 * - 输出：一组类型安全的 API 方法
 * - 优势：不依赖具体的请求实现（axios、uni.request 等），可以在 Web 和 App 端复用
 * 
 * @param client - HTTP 请求客户端，必须实现 IRequestClient 接口
 *   - 可以是 Web 端的 axios 适配器
 *   - 可以是 App 端的 uni.request 适配器
 *   - 可以是测试用的 Mock 客户端
 * 
 * @returns API 方法集合对象，包含所有 Purchase 相关的请求方法
 * 
 * @example
 * ```typescript
 * // Web 端使用
 * import { createPurchaseApi } from '@bayes-mes/shared/api/purchase/factory'
 * import { webAdapter } from '#/api/adapters'
 * 
 * const purchaseApi = createPurchaseApi(webAdapter)
 * const suppliers = await purchaseApi.getSupplierList({ page: 1 })
 * ```
 */
export function createPurchaseApi(client: IRequestClient) {
  // ===== 返回 API 方法集合 =====
  return {
    // ============================================================================
    // ========== 供应商管理 API ==========
    // ============================================================================
    
    /**
     * 获取供应商列表（分页）
     * 
     * 使用场景：
     * - 在供应商管理页面的主列表中使用
     * - 支持分页、搜索、筛选功能（按供应商类型、质量等级、状态、是否黑名单等）
     * - 用户可以查看所有供应商信息
     * 
     * @param params - 查询参数对象（可选）
     *   - page?: number - 页码，从 1 开始，默认 1
     *   - per_page?: number - 每页数量，默认 15，最大 100
     *   - search?: string - 搜索关键词，搜索供应商编码或名称（模糊匹配）
     *     示例：search: 'ABC'（搜索编码或名称包含"ABC"的供应商）
     *   - supplier_type?: SupplierType - 供应商类型筛选
     *     可选值：1-原材料, 2-半成品, 3-MRO, 4-服务
     *   - quality_level?: QualityLevel - 质量等级筛选
     *     可选值：1-A级, 2-B级, 3-C级, 4-D级
     *   - status?: SupplierStatus - 状态筛选
     *     可选值：0-禁用, 1-启用
     *   - is_blacklisted?: 0 | 1 - 是否黑名单筛选
     *     可选值：0-否, 1-是
     *   - is_designated?: 0 | 1 - 是否客户指定筛选
     *     可选值：0-否, 1-是
     *   - sort_by?: string - 排序字段
     *     示例：sort_by: 'created_at'（按创建时间排序）
     *   - sort_order?: 'asc' | 'desc' - 排序方向
     *     示例：sort_order: 'desc'（降序）
     * 
     * @returns Promise<PaginationResult<Supplier>> 分页结果对象
     *   返回的数据结构：
     *   {
     *     data: Supplier[],        // 供应商列表数组
     *     total: number,           // 总记录数
     *     current_page: number,    // 当前页码
     *     per_page: number,        // 每页数量
     *     last_page: number,       // 最后一页页码
     *     from: number,            // 当前页起始记录号
     *     to: number               // 当前页结束记录号
     *   }
     * 
     * 数据流向：
     * 1. 用户操作：用户在供应商管理页面点击"查询"或输入搜索关键词
     * 2. 前端调用：组件调用 purchaseApi.getSupplierList({ page: 1, search: 'ABC' })
     * 3. 适配器处理：适配器将参数转换为 HTTP 请求
     *    - Web端：axios 发送 GET 请求
     *    - App端：uni.request 发送 GET 请求
     * 4. 网络传输：发送到后端 GET /api/v1/purchase/suppliers?page=1&search=ABC
     * 5. 后端处理：
     *    - 路由匹配到 SupplierController@index 方法
     *    - 验证查询参数（SupplierQueryParams）
     *    - 查询数据库，应用筛选条件和分页
     *    - 通过 SupplierResource 格式化数据（snake_case → camelCase）
     * 6. 响应返回：后端返回 JSON 格式的分页数据
     * 7. 适配器解包：适配器提取 response.data.data（实际数据）
     * 8. 前端接收：组件收到 PaginationResult<Supplier> 类型的数据
     * 9. 页面更新：使用数据更新表格显示
     * 
     * 代码示例：
     * ```typescript
     * // 示例1：获取第一页（使用默认参数）
     * const page1 = await purchaseApi.getSupplierList()
     * console.log(page1.data)  // Supplier对象数组
     * console.log(page1.total) // 总记录数
     * 
     * // 示例2：搜索功能
     * const searchResult = await purchaseApi.getSupplierList({
     *   search: 'ABC'
     * })
     * 
     * // 示例3：组合查询（分页 + 搜索 + 筛选 + 排序）
     * const result = await purchaseApi.getSupplierList({
     *   page: 1,
     *   per_page: 20,
     *   search: '原材料',
     *   supplier_type: 1,  // 原材料类型
     *   quality_level: 1,  // A级
     *   status: 1,         // 启用
     *   sort_by: 'created_at',
     *   sort_order: 'desc'
     * })
     * ```
     * 
     * 错误处理：
     * - 404：资源不存在（路由错误或后端未实现）
     * - 422：参数验证失败（page 不是数字等）
     * - 500：服务器内部错误（数据库查询失败等）
     */
    getSupplierList(params?: SupplierQueryParams) {
      return client.get<PaginationResult<Supplier>>(
        API_ROUTES.purchase.list,  // '/api/v1/purchase/suppliers'
        { params }
      )
    },
    
    /**
     * 获取所有供应商（不分页）
     * 
     * 使用场景：
     * - 在下拉选择框中显示所有启用状态的供应商
     * - 不需要分页，直接获取所有数据
     * - 通常用于选择器组件
     * 
     * @returns Promise<Supplier[]> 供应商数组（仅包含启用状态的供应商）
     * 
     * 数据流向：
     * 1. 前端调用：purchaseApi.getAllSuppliers()
     * 2. 发送请求：GET /api/v1/purchase/suppliers/all
     * 3. 后端处理：SupplierController@all 方法，查询所有启用状态的供应商
     * 4. 返回数据：Supplier[] 数组
     * 
     * 代码示例：
     * ```typescript
     * // 获取所有启用状态的供应商（用于下拉选择）
     * const suppliers = await purchaseApi.getAllSuppliers()
     * 
     * // 在下拉组件中使用
     * const supplierOptions = suppliers.map(s => ({
     *   label: s.supplierName,
     *   value: s.id
     * }))
     * ```
     */
    getAllSuppliers() {
      return client.get<Supplier[]>(
        API_ROUTES.purchase.all  // '/api/v1/purchase/suppliers/all'
      )
    },
    
    /**
     * 获取活跃供应商列表
     * 
     * 使用场景：
     * - 获取所有启用且非黑名单的供应商
     * - 用于快速筛选优质供应商
     * 
     * @returns Promise<Supplier[]> 活跃供应商数组
     */
    getActiveSuppliers() {
      return client.get<Supplier[]>(
        API_ROUTES.purchase.active  // '/api/v1/purchase/suppliers/active'
      )
    },
    
    /**
     * 获取黑名单供应商列表
     * 
     * 使用场景：
     * - 查看所有被加入黑名单的供应商
     * - 用于供应商管理和审核
     * 
     * @returns Promise<Supplier[]> 黑名单供应商数组
     */
    getBlacklistedSuppliers() {
      return client.get<Supplier[]>(
        API_ROUTES.purchase.blacklisted  // '/api/v1/purchase/suppliers/blacklisted'
      )
    },
    
    /**
     * 获取供应商汇总统计
     * 
     * 使用场景：
     * - 在供应商管理页面顶部显示统计卡片
     * - 展示供应商总数、按类型分布、按质量等级分布等
     * 
     * @returns Promise<SupplierGlobalStatistics> 统计信息对象
     *   返回的数据结构：
     *   {
     *     total: number,           // 供应商总数
     *     active: number,         // 启用数量
     *     inactive: number,        // 禁用数量
     *     blacklisted: number,     // 黑名单数量
     *     designated: number,      // 客户指定数量
     *     byType: {               // 按类型分布
     *       rawMaterial: number,
     *       semiFinished: number,
     *       mro: number,
     *       service: number,
     *     },
     *     byQualityLevel: {       // 按质量等级分布
     *       a: number,
     *       b: number,
     *       c: number,
     *       d: number,
     *     }
     *   }
     * 
     * 代码示例：
     * ```typescript
     * // 获取供应商统计信息
     * const stats = await purchaseApi.getSupplierStatistics()
     * console.log(`供应商总数：${stats.total}`)
     * console.log(`启用数量：${stats.active}`)
     * ```
     */
    getSupplierStatistics() {
      return client.get<Record<string, any>>(
        API_ROUTES.purchase.statistics  // '/api/v1/purchase/suppliers/statistics'
      )
    },
    
    /**
     * 创建供应商
     * 
     * 使用场景：
     * - 在供应商管理页面点击"新建"按钮
     * - 提交创建供应商的表单
     * 
     * @param data - 创建供应商的表单数据
     *   字段要求（参考 CreateSupplierForm 类型）：
     *   - supplier_name: string - 供应商名称（必填）
     *   - supplier_type: number - 供应商类型（必填）：1-原材料, 2-半成品, 3-MRO, 4-服务
     *   - ... 其他可选字段（联系方式、地址、资质信息、财务信息等）
     * 
     * @returns Promise<Supplier> 创建成功后的供应商对象（包含自动生成的编码和ID）
     * 
     * 数据流向：
     * 1. 用户填写创建表单，点击"提交"按钮
     * 2. 前端验证：表单验证（必填项、格式等）
     * 3. 前端调用：purchaseApi.createSupplier({ supplier_name: '测试供应商', supplier_type: 1 })
     * 4. 发送请求：POST /api/v1/purchase/suppliers
     *    请求体：{ supplier_name: '测试供应商', supplier_type: 1, ... }（JSON格式，snake_case）
     * 5. 后端处理：SupplierController@store 方法
     *    - 验证数据（使用 CreateSupplierRequest）
     *    - 自动生成供应商编码（如：SUP-R-000001）
     *    - 插入数据库
     *    - 返回新创建的记录（包含自动生成的编码和ID）
     * 6. 前端处理：
     *    - 显示成功提示
     *    - 跳转到列表页或详情页
     *    - 刷新列表数据
     * 
     * 代码示例：
     * ```typescript
     * // 创建新供应商
     * const newSupplier: CreateSupplierForm = {
     *   supplier_name: '测试供应商',
     *   supplier_type: 1,  // 原材料
     *   contact_person: '张三',
     *   contact_phone: '13800138000',
     *   // ... 其他字段
     * }
     * 
     * const created = await purchaseApi.createSupplier(newSupplier)
     * console.log(created.supplierCode)  // 自动生成的编码，如 'SUP-R-000001'
     * console.log(created.id)            // 新创建的 ID
     * ```
     * 
     * 错误处理：
     * - 422：数据验证失败（必填字段缺失、格式错误等）
     * - 403：无权限创建
     * - 500：服务器错误（数据库插入失败）
     */
    createSupplier(data: CreateSupplierForm) {
      return client.post<Supplier>(
        API_ROUTES.purchase.suppliers,  // '/api/v1/purchase/suppliers'
        data
      )
    },
    
    /**
     * 获取供应商详情
     * 
     * 使用场景：
     * - 在供应商详情页面显示完整信息
     * - 编辑供应商时获取当前数据
     * - 查看供应商的详细资料（包含关联的物料价格和评分）
     * 
     * @param id - 供应商的唯一标识符（主键）
     *   示例：id: 123（获取 ID 为 123 的供应商）
     * 
     * @returns Promise<Supplier> 单个供应商对象（包含关联的物料价格列表和评分记录）
     * 
     * 数据流向：
     * 1. 用户点击列表中的某一行，或在 URL 中输入详情页地址
     * 2. 前端调用：purchaseApi.getSupplierDetail(123)
     * 3. 发送请求：GET /api/v1/purchase/suppliers/123
     * 4. 后端处理：SupplierController@show 方法
     *    - 验证 ID 参数
     *    - 从数据库查询指定 ID 的记录（包含关联的物料价格和评分）
     *    - 如果不存在，返回 404
     *    - 通过 SupplierResource 格式化数据
     * 5. 返回数据：单个 Supplier 对象（包含 materials 和 ratings 字段）
     * 6. 前端显示：在详情页或编辑表单中展示数据
     * 
     * 代码示例：
     * ```typescript
     * // 获取 ID 为 123 的供应商
     * const supplier = await purchaseApi.getSupplierDetail(123)
     * console.log(supplier.supplierName)  // 供应商名称
     * console.log(supplier.supplierCode) // 供应商编码
     * console.log(supplier.materials)     // 供应物料价格列表
     * console.log(supplier.ratings)      // 历史评分记录
     * ```
     * 
     * 错误处理：
     * - 404：供应商不存在
     * - 403：无权限访问该供应商
     * - 422：ID 格式错误（不是数字）
     */
    getSupplierDetail(id: number) {
      return client.get<Supplier>(
        API_ROUTES.purchase.detail(id)  // '/api/v1/purchase/suppliers/123'
      )
    },
    
    /**
     * 根据编码获取供应商
     * 
     * 使用场景：
     * - 通过供应商编码快速查找供应商
     * - 在扫码或输入编码的场景中使用
     * 
     * @param code - 供应商编码（如：SUP-R-000001）
     * 
     * @returns Promise<Supplier> 供应商对象
     * 
     * 代码示例：
     * ```typescript
     * // 根据编码查找供应商
     * const supplier = await purchaseApi.getSupplierByCode('SUP-R-000001')
     * ```
     */
    getSupplierByCode(code: string) {
      return client.get<Supplier>(
        API_ROUTES.purchase.findByCode(code)  // '/api/v1/purchase/suppliers/code/SUP-R-000001'
      )
    },
    
    /**
     * 更新供应商
     * 
     * 使用场景：
     * - 在供应商编辑页面修改后提交
     * - 更新供应商的部分字段（如联系方式、地址、质量等级等）
     * 
     * @param id - 要更新的供应商的 ID
     * @param data - 更新的数据（只需要包含要修改的字段）
     *   字段要求（参考 UpdateSupplierForm 类型）：
     *   - 可以只包含部分字段（部分更新）
     *   - 不能修改的字段：id, supplierCode, createdAt（后端会忽略）
     * 
     * @returns Promise<Supplier> 更新后的完整供应商对象
     * 
     * 代码示例：
     * ```typescript
     * // 更新供应商的联系方式
     * const updated = await purchaseApi.updateSupplier(123, {
     *   contact_person: '李四',
     *   contact_phone: '13900139000'
     * })
     * ```
     * 
     * 错误处理：
     * - 404：供应商不存在
     * - 422：数据验证失败
     * - 403：无权限修改
     */
    updateSupplier(id: number, data: UpdateSupplierForm) {
      return client.put<Supplier>(
        API_ROUTES.purchase.purchaseSuppliers(id),  // '/api/v1/purchase/suppliers/123'
        data
      )
    },
    
    /**
     * 删除供应商
     * 
     * 使用场景：
     * - 在供应商列表中点击"删除"按钮
     * - 在供应商详情页删除当前记录
     * 
     * @param id - 要删除的供应商的 ID
     * 
     * @returns Promise<void> 删除成功不返回数据
     * 
     * 错误处理：
     * - 404：供应商不存在（可能已被删除）
     * - 403：无权限删除
     * - 409：无法删除（存在关联数据，如存在采购订单）
     */
    deleteSupplier(id: number) {
      return client.delete<void>(
        API_ROUTES.purchase.delete(id)  // '/api/v1/purchase/suppliers/123'
      )
    },
    
    /**
     * 批量删除供应商
     * 
     * 使用场景：
     * - 在供应商列表中选中多个供应商，批量删除
     * 
     * @param ids - 要删除的供应商 ID 数组
     *   示例：ids: [1, 2, 3]（删除 ID 为 1、2、3 的供应商）
     * 
     * @returns Promise<void> 删除成功不返回数据
     * 
     * 代码示例：
     * ```typescript
     * // 批量删除供应商
     * await purchaseApi.batchDeleteSuppliers([1, 2, 3])
     * ```
     */
    batchDeleteSuppliers(ids: number[]) {
      return client.delete<void>(
        API_ROUTES.purchase.batch,  // '/api/v1/purchase/suppliers/batch'
        { data: { ids } }
      )
    },
    
    /**
     * 获取供应商详情统计信息
     * 
     * 使用场景：
     * - 在供应商详情页显示该供应商的采购统计信息
     * - 展示年度采购额、历史采购额、订单数、合格率、交期达成率、质量评分等
     * 
     * @param id - 供应商 ID
     * 
     * @returns Promise<SupplierStatistics> 供应商统计信息
     *   返回的数据结构：
     *   {
     *     yearAmount: number,      // 年度采购总额
     *     totalAmount: number,     // 历史采购总额
     *     yearOrders: number,      // 年度订单数
     *     totalOrders: number,     // 历史订单总数
     *     qualityRate: number,     // 合格率（%）
     *     deliveryRate: number,   // 交期达成率（%）
     *     qualityScore: number | null  // 质量评分（0-100）
     *   }
     * 
     * 代码示例：
     * ```typescript
     * // 获取供应商统计信息
     * const stats = await purchaseApi.getSupplierDetailStatistics(123)
     * console.log(`年度采购额：${stats.yearAmount}`)
     * console.log(`合格率：${stats.qualityRate}%`)
     * ```
     */
    getSupplierDetailStatistics(id: number) {
      return client.get<SupplierStatistics>(
        API_ROUTES.purchase.suppliersStatistics(id)  // '/api/v1/purchase/suppliers/123/statistics'
      )
    },
    
    /**
     * 导出供应商列表
     * 
     * 使用场景：
     * - 导出符合条件的供应商数据为 Excel 文件
     * - 支持按查询条件筛选导出
     * 
     * @param params - 查询参数（与 getSupplierList 相同）
     * 
     * @returns Promise<Blob> 文件数据（二进制）
     * 
     * 代码示例：
     * ```typescript
     * // 导出所有供应商
     * const blob = await purchaseApi.exportSuppliers()
     * 
     * // 下载文件
     * const url = URL.createObjectURL(blob)
     * const a = document.createElement('a')
     * a.href = url
     * a.download = 'suppliers.xlsx'
     * a.click()
     * ```
     */
    exportSuppliers(params?: SupplierQueryParams) {
      return client.post<Blob>(
        API_ROUTES.purchase.export,  // '/api/v1/purchase/suppliers/export'
        params,
        { responseType: 'blob' }
      )
    },
    
    /**
     * 导入供应商列表
     *
     * 使用场景：
     * - 批量导入供应商数据
     * - 前端解析Excel后传递JSON数据数组
     *
     * 导入语义：全量或全不——任何一条预检失败则一条不导入，返回行级失败明细。
     *
     * @param data - 供应商数据数组（前端已完成字段映射和值转换）
     *
     * @returns Promise<BatchImportResult> 标准批量导入结果（经 BatchOperationResource 输出）
     *
     * 代码示例：
     * ```typescript
     * // 前端解析Excel后调用
     * const handleImport = async (parsedData: any[]) => {
     *   const result = await purchaseApi.importSuppliers(parsedData)
     *   console.log(`成功导入 ${result.successCount} 条`)
     *   console.log(`失败 ${result.failedCount} 条`)
     * }
     * ```
     */
    importSuppliers(data: any[]) {
      return client.post<BatchImportResult>(
        API_ROUTES.purchase.import,  // '/api/v1/purchase/suppliers/import'
        { data }
      )
    },
    
    // ============================================================================
    // ========== 供应商物料价格管理 API ==========
    // ============================================================================
    
    /**
     * 获取供应商的供应物料列表
     * 
     * 使用场景：
     * - 在供应商详情页的"供应物料"标签页中显示
     * - 展示该供应商供应的所有物料及其当前价格
     * 
     * @param supplierId - 供应商 ID
     * 
     * @returns Promise<SupplierMaterial[]> 供应物料价格数组（仅包含当前价格）
     * 
     * 代码示例：
     * ```typescript
     * // 获取供应商的供应物料列表
     * const materials = await purchaseApi.getSupplierMaterials(123)
     * materials.forEach(m => {
     *   console.log(`${m.materialName}: ${m.currentPrice} ${m.currency}/${m.unit}`)
     * })
     * ```
     */
    getSupplierMaterials(supplierId: number) {
      return client.get<SupplierMaterial[]>(
        API_ROUTES.purchase.suppliersMaterials(supplierId)  // '/api/v1/purchase/suppliers/123/materials'
      )
    },
    
    /**
     * 添加供应物料价格
     * 
     * 使用场景：
     * - 为供应商添加新的供应物料及其价格信息
     * - 在供应商详情页的"供应物料"标签页中点击"添加"
     * 
     * @param supplierId - 供应商 ID
     * @param data - 添加物料价格的表单数据
     *   字段要求（参考 AddSupplierMaterialForm 类型）：
     *   - material_id: number - 物料ID（必填）
     *   - current_price: number - 当前价格（必填）
     *   - currency: string - 币种（必填，如：CNY、USD）
     *   - unit: string - 计价单位（必填，如：PCS、KG）
     *   - min_order_quantity?: number - 最小起订量（可选）
     *   - delivery_days?: number - 交货周期（天数，可选）
     *   - effective_date?: string - 生效日期（可选，YYYY-MM-DD格式）
     *   - remark?: string - 备注（可选）
     * 
     * @returns Promise<SupplierMaterial> 创建的供应物料价格对象
     * 
     * 数据流向：
     * 1. 用户选择物料，填写价格信息，点击"提交"
     * 2. 前端调用：purchaseApi.addSupplierMaterial(123, { material_id: 456, current_price: 100, ... })
     * 3. 发送请求：POST /api/v1/purchase/suppliers/123/materials
     * 4. 后端处理：SupplierMaterialController@store 方法
     *    - 验证数据
     *    - 检查该物料是否已存在当前价格（不允许重复）
     *    - 插入数据库（自动填充物料编码、物料名称等冗余字段）
     *    - 记录审计日志
     * 5. 返回数据：新创建的 SupplierMaterial 对象
     * 
     * 代码示例：
     * ```typescript
     * // 为供应商添加供应物料
     * const newMaterial = await purchaseApi.addSupplierMaterial(123, {
     *   material_id: 456,
     *   current_price: 100,
     *   currency: 'CNY',
     *   unit: 'PCS',
     *   min_order_quantity: 100,
     *   delivery_days: 7,
     *   effective_date: '2025-01-01'
     * })
     * ```
     * 
     * 错误处理：
     * - 422：数据验证失败或该物料已存在当前价格
     * - 404：供应商或物料不存在
     * - 403：无权限
     */
    addSupplierMaterial(supplierId: number, data: AddSupplierMaterialForm) {
      return client.post<SupplierMaterial>(
        API_ROUTES.purchase.materials(supplierId),  // '/api/v1/purchase/suppliers/123/materials'
        data
      )
    },
    
    /**
     * 更新物料价格（归档旧价格，创建新价格记录）
     * 
     * 使用场景：
     * - 更新供应商的物料价格
     * - 价格变动时保留历史记录（旧价格被归档，新价格作为当前价格）
     * 
     * @param id - 供应物料价格记录的 ID
     * @param data - 更新的价格数据
     *   字段要求（参考 UpdateSupplierMaterialForm 类型）：
     *   - current_price: number - 新价格（必填）
     *   - currency?: string - 币种（可选）
     *   - unit?: string - 计价单位（可选）
     *   - min_order_quantity?: number - 最小起订量（可选）
     *   - delivery_days?: number - 交货周期（可选）
     *   - effective_date?: string - 生效日期（可选）
     *   - remark?: string - 备注（可选）
     * 
     * @returns Promise<SupplierMaterial> 新创建的价格记录对象（旧价格已被归档）
     * 
     * 数据流向：
     * 1. 用户在价格列表中点击"修改价格"
     * 2. 前端调用：purchaseApi.updateSupplierMaterial(789, { current_price: 120 })
     * 3. 发送请求：PUT /api/v1/purchase/supplier-materials/789
     * 4. 后端处理：SupplierMaterialController@update 方法
     *    - 找到旧价格记录
     *    - 将旧价格设为非当前（is_current = 0），设置失效日期
     *    - 创建新价格记录（is_current = 1），设置生效日期
     *    - 记录审计日志
     * 5. 返回数据：新创建的价格记录
     * 
     * 代码示例：
     * ```typescript
     * // 更新物料价格（价格从100涨到120）
     * const newPrice = await purchaseApi.updateSupplierMaterial(789, {
     *   current_price: 120,
     *   effective_date: '2025-01-01'
     * })
     * 
     * // 旧价格记录会被归档，新价格记录作为当前价格
     * ```
     * 
     * 错误处理：
     * - 404：价格记录不存在
     * - 422：数据验证失败
     * - 403：无权限
     */
    updateSupplierMaterial(id: number, data: UpdateSupplierMaterialForm) {
      return client.put<SupplierMaterial>(
        API_ROUTES.purchase.supplierMaterials(id),  // '/api/v1/purchase/supplier-materials/789'
        data
      )
    },
    
    /**
     * 删除供应物料价格
     * 
     * 使用场景：
     * - 从供应商的供应物料列表中移除某个物料
     * 
     * @param id - 供应物料价格记录的 ID
     * 
     * @returns Promise<void> 删除成功不返回数据
     * 
     * 代码示例：
     * ```typescript
     * // 删除供应物料价格
     * await purchaseApi.deleteSupplierMaterial(789)
     * ```
     */
    deleteSupplierMaterial(id: number) {
      return client.delete<void>(
        API_ROUTES.purchase.purchaseSupplierMaterials(id)  // '/api/v1/purchase/supplier-materials/789'
      )
    },
    
    /**
     * 获取物料历史价格
     * 
     * 使用场景：
     * - 查看某个物料的价格变动历史
     * - 在价格详情页显示历史价格记录
     * 
     * @param id - 当前价格记录的 ID
     * 
     * @returns Promise<SupplierMaterial[]> 该物料的所有历史价格记录（按生效日期降序）
     * 
     * 代码示例：
     * ```typescript
     * // 获取物料价格历史
     * const history = await purchaseApi.getSupplierMaterialHistory(789)
     * history.forEach(h => {
     *   console.log(`${h.effectiveDate}: ${h.currentPrice}`)
     * })
     * ```
     */
    getSupplierMaterialHistory(id: number) {
      return client.get<SupplierMaterial[]>(
        API_ROUTES.purchase.supplierMaterialsHistory(id)  // '/api/v1/purchase/supplier-materials/789/history'
      )
    },
    
    // ============================================================================
    // ========== 采购订单管理 API ==========
    // ============================================================================
    
    /**
     * 获取采购订单列表（分页，带汇总）
     * 
     * 使用场景：
     * - 在采购订单管理页面的主列表中使用
     * - 支持分页、搜索、筛选功能（按供应商、状态、优先级、日期范围等）
     * - 列表底部显示汇总数据（订单总数、总金额、已收货金额、待收货金额、逾期订单数等）
     * 
     * @param params - 查询参数对象（可选）
     *   - page?: number - 页码，从 1 开始，默认 1
     *   - per_page?: number - 每页数量，默认 15，最大 100
     *   - search?: string - 搜索关键词，搜索订单号（模糊匹配）
     *   - supplier_id?: number - 供应商ID筛选
     *   - status?: PurchaseOrderStatus | PurchaseOrderStatus[] - 状态筛选（支持多选）
     *     可选值：1-草稿, 2-待审核, 3-已审核, 4-部分收货, 5-已完成, 6-已关闭, 7-已取消, 8-待取消审核
     *   - priority?: Priority | Priority[] - 优先级筛选（支持多选）
     *     可选值：1-低, 2-中, 3-高, 4-紧急
     *   - order_date_start?: string - 订单日期开始（YYYY-MM-DD）
     *   - order_date_end?: string - 订单日期结束（YYYY-MM-DD）
     *   - required_date_start?: string - 要求交期开始（YYYY-MM-DD）
     *   - required_date_end?: string - 要求交期结束（YYYY-MM-DD）
     *   - is_overdue?: boolean - 是否逾期筛选（true-仅逾期, false-仅未逾期）
     *   - sort_by?: string - 排序字段
     *   - sort_order?: 'asc' | 'desc' - 排序方向
     * 
     * @returns Promise<PaginationResult<PurchaseOrder> & { summary: OrderSummary }> 分页结果对象（包含汇总数据）
     *   返回的数据结构：
     *   {
     *     data: PurchaseOrder[],      // 订单列表数组
     *     total: number,               // 总记录数
     *     current_page: number,        // 当前页码
     *     per_page: number,            // 每页数量
     *     last_page: number,           // 最后一页页码
     *     from: number,                // 当前页起始记录号
     *     to: number,                  // 当前页结束记录号
     *     summary: {                   // 汇总数据（列表底部显示）
     *       totalOrders: number,       // 订单总数
     *       totalAmount: number,       // 订单总金额
     *       receivedAmount: number,    // 已收货金额
     *       pendingAmount: number,     // 待收货金额
     *       overdueOrders: number     // 逾期订单数
     *     }
     *   }
     * 
     * 代码示例：
     * ```typescript
     * // 获取订单列表（带汇总）
     * const result = await purchaseApi.getPurchaseOrderList({ page: 1 })
     * console.log(result.data)        // 订单列表
     * console.log(result.summary)     // 汇总数据
     * console.log(result.summary.totalAmount)  // 订单总金额
     * ```
     */
    getPurchaseOrderList(params?: PurchaseOrderQueryParams) {
      return client.get<PaginationResult<PurchaseOrder> & { summary?: Record<string, any> }>(
        API_ROUTES.purchase.orders,  // '/api/v1/purchase/orders'
        { params }
      )
    },
    
    /**
     * 获取采购订单统计信息
     * 
     * 使用场景：
     * - 在采购订单管理页面顶部显示统计卡片
     * - 展示订单总数、各状态订单数、总金额、逾期订单数等
     * 
     * @returns Promise<PurchaseOrderStatistics> 订单统计信息
     */
    getPurchaseOrderStatistics() {
      return client.get<Record<string, any>>(
        API_ROUTES.purchase.ordersStatistics  // '/api/v1/purchase/orders/statistics'
      )
    },
    
    /**
     * 获取逾期订单列表
     * 
     * 使用场景：
     * - 查看所有逾期的订单
     * - 用于催单和跟踪管理
     * 
     * @param params - 分页参数（可选）
     * 
     * @returns Promise<PaginationResult<PurchaseOrder>> 逾期订单分页结果
     */
    getOverduePurchaseOrders(params?: { page?: number; per_page?: number }) {
      return client.get<PaginationResult<PurchaseOrder>>(
        API_ROUTES.purchase.overdue,  // '/api/v1/purchase/orders/overdue'
        { params }
      )
    },
    
    /**
     * 创建采购订单
     * 
     * 使用场景：
     * - 在采购订单管理页面点击"新建"按钮
     * - 提交创建采购订单的表单
     * 
     * @param data - 创建采购订单的表单数据
     *   字段要求（参考 CreatePurchaseOrderForm 类型）：
     *   - supplier_id: number - 供应商ID（必填）
     *   - order_date: string - 订单日期（必填，YYYY-MM-DD）
     *   - promise_date: string - 承诺交期（必填，YYYY-MM-DD）
     *   - required_date?: string - 要求交期（可选，YYYY-MM-DD）
     *   - priority?: number - 优先级（可选）：1-低, 2-中, 3-高, 4-紧急
     *   - source_type?: number - 来源类型（可选）：1-手动创建, 2-销售订单, 3-生产计划
     *   - tax_rate?: number - 税率（可选，如：0.13 表示13%）
     *   - remark?: string - 备注（可选）
     *   - items: PurchaseOrderItemForm[] - 订单明细数组（必填）
     *     - material_id: number - 物料ID（必填）
     *     - quantity: number - 数量（必填）
     *     - unit_price: number - 单价（必填）
     *     - unit: string - 单位（必填）
     *     - line_remark?: string - 行备注（可选）
     * 
     * @returns Promise<PurchaseOrder> 创建成功后的订单对象（包含自动生成的订单号和ID）
     * 
     * 数据流向：
     * 1. 用户填写订单表单（选择供应商、填写订单明细等），点击"提交"
     * 2. 前端验证：表单验证（必填项、日期格式等）
     * 3. 前端调用：purchaseApi.createPurchaseOrder({ supplier_id: 123, order_date: '2025-01-01', items: [...], ... })
     * 4. 发送请求：POST /api/v1/purchase/orders
     * 5. 后端处理：PurchaseOrderController@store 方法
     *    - 验证数据（使用 CreatePurchaseOrderRequest）
     *    - 自动生成订单号（如：PO-20250104-000001）
     *    - 计算订单总金额、总数量
     *    - 插入订单主表和明细表
     *    - 返回新创建的订单（包含订单号和ID）
     * 6. 前端处理：
     *    - 显示成功提示
     *    - 跳转到订单详情页
     *    - 刷新列表数据
     * 
     * 代码示例：
     * ```typescript
     * // 创建采购订单
     * const newOrder: CreatePurchaseOrderForm = {
     *   supplier_id: 123,
     *   order_date: '2025-01-01',
     *   promise_date: '2025-01-15',
     *   priority: 2,  // 中优先级
     *   items: [
     *     {
     *       material_id: 456,
     *       quantity: 100,
     *       unit_price: 10,
     *       unit: 'PCS'
     *     }
     *   ]
     * }
     * 
     * const created = await purchaseApi.createPurchaseOrder(newOrder)
     * console.log(created.orderNo)  // 自动生成的订单号，如 'PO-20250104-000001'
     * ```
     * 
     * 错误处理：
     * - 422：数据验证失败
     * - 404：供应商或物料不存在
     * - 403：无权限创建
     * - 500：服务器错误
     */
    createPurchaseOrder(data: CreatePurchaseOrderForm) {
      return client.post<PurchaseOrder>(
        API_ROUTES.purchase.orders,  // '/api/v1/purchase/orders'
        data
      )
    },
    
    /**
     * 获取采购订单详情
     * 
     * 使用场景：
     * - 在采购订单详情页面显示完整信息
     * - 编辑订单时获取当前数据
     * - 查看订单的详细资料（包含订单明细、收货记录、评分等）
     * 
     * @param id - 采购订单的唯一标识符（主键）
     * 
     * @returns Promise<PurchaseOrder> 单个订单对象（包含关联的明细、收货记录、评分）
     * 
     * 代码示例：
     * ```typescript
     * // 获取订单详情
     * const order = await purchaseApi.getPurchaseOrderDetail(123)
     * console.log(order.orderNo)    // 订单号
     * console.log(order.items)      // 订单明细数组
     * console.log(order.receivings) // 收货记录数组
     * console.log(order.rating)     // 评分记录（如果有）
     * ```
     */
    getPurchaseOrderDetail(id: number) {
      return client.get<PurchaseOrder>(
        API_ROUTES.purchase.purchaseOrders(id)  // '/api/v1/purchase/orders/123'
      )
    },
    
    /**
     * 更新采购订单
     * 
     * 使用场景：
     * - 在订单编辑页面修改后提交（仅草稿状态可修改）
     * - 更新订单的部分字段（如订单日期、交期、优先级、订单明细等）
     * 
     * @param id - 要更新的订单的 ID
     * @param data - 更新的数据
     * 
     * @returns Promise<PurchaseOrder> 更新后的完整订单对象
     * 
     * 错误处理：
     * - 404：订单不存在
     * - 422：数据验证失败或订单状态不允许修改（非草稿状态）
     * - 403：无权限修改
     */
    updatePurchaseOrder(id: number, data: UpdatePurchaseOrderForm) {
      // ⚠️ 注意：routes.ts 中没有单独的 update 路由，使用 purchaseOrders(id) 作为 PUT 方法
      // 但根据实际路由，应该是 PUT /api/v1/purchase/orders/{id}
      // 检查 routes.ts 中的定义
      return client.put<PurchaseOrder>(
        API_ROUTES.purchase.purchaseOrders(id),  // '/api/v1/purchase/orders/123'
        data
      )
    },
    
    /**
     * 删除采购订单
     * 
     * 使用场景：
     * - 在订单列表中点击"删除"按钮（仅草稿状态可删除）
     * 
     * @param id - 要删除的订单的 ID
     * 
     * @returns Promise<void> 删除成功不返回数据
     * 
     * 错误处理：
     * - 404：订单不存在
     * - 403：无权限删除或订单状态不允许删除（非草稿状态）
     */
    deletePurchaseOrder(id: number) {
      return client.delete<void>(
        API_ROUTES.purchase.purchaseOrders(id)  // '/api/v1/purchase/orders/123'
      )
    },
    
    /**
     * 审核订单
     * 
     * 使用场景：
     * - 在订单审核页面审核订单（通过或拒绝）
     * - 订单状态从"待审核"变为"已审核"或保持"待审核"（拒绝时）
     * 
     * @param id - 订单 ID
     * @param data - 审核数据
     *   - approve: boolean - 是否通过（true-通过, false-拒绝）
     *   - note?: string - 审核意见（可选）
     * 
     * @returns Promise<PurchaseOrder> 审核后的订单对象
     * 
     * 代码示例：
     * ```typescript
     * // 审核通过订单
     * const approved = await purchaseApi.approvePurchaseOrder(123, {
     *   approve: true,
     *   note: '审核通过'
     * })
     * 
     * // 审核拒绝订单
     * const rejected = await purchaseApi.approvePurchaseOrder(123, {
     *   approve: false,
     *   note: '价格过高，拒绝'
     * })
     * ```
     */
    approvePurchaseOrder(id: number, data: { approve: boolean; note?: string }) {
      return client.put<PurchaseOrder>(
        API_ROUTES.purchase.approveApplication(id),  // '/api/v1/purchase/orders/123/approve'
        data
      )
    },
    
    /**
     * 确认订单
     * 
     * 使用场景：
     * - 确认订单（订单状态变为"已确认"）
     * 
     * @param id - 订单 ID
     * 
     * @returns Promise<PurchaseOrder> 确认后的订单对象
     */
    confirmPurchaseOrder(id: number) {
      return client.put<PurchaseOrder>(
        API_ROUTES.purchase.confirm(id)  // '/api/v1/purchase/orders/123/confirm'
      )
    },
    
    /**
     * 取消订单（需审批）
     * 
     * 使用场景：
     * - 取消订单（订单状态变为"待取消审核"或"已取消"）
     * - 需提供取消原因
     * 
     * @param id - 订单 ID
     * @param data - 取消数据
     *   - cancel_reason?: string - 取消原因（可选）
     * 
     * @returns Promise<PurchaseOrder> 取消后的订单对象
     */
    cancelPurchaseOrder(id: number, data?: { cancel_reason?: string }) {
      return client.put<PurchaseOrder>(
        API_ROUTES.purchase.cancel(id),  // '/api/v1/purchase/orders/123/cancel'
        data
      )
    },
    
    /**
     * 关闭订单
     * 
     * 使用场景：
     * - 手动关闭订单（订单状态变为"已关闭"）
     * - 通常用于订单已完全收货且不再需要后续操作时
     * 
     * @param id - 订单 ID
     * 
     * @returns Promise<PurchaseOrder> 关闭后的订单对象
     */
    closePurchaseOrder(id: number) {
      return client.put<PurchaseOrder>(
        API_ROUTES.purchase.close(id)  // '/api/v1/purchase/orders/123/close'
      )
    },
    
    /**
     * 订单评分
     * 
     * 使用场景：
     * - 在订单完成收货后对供应商进行评分
     * - 在订单详情页的"评分"功能中使用
     * 
     * @param id - 订单 ID
     * @param data - 评分数据
     *   字段要求（参考 RateOrderForm 类型）：
     *   - quality_score: number - 质量评分（必填，0-100）
     *   - delivery_score?: number - 交期评分（可选，0-100）
     *   - service_score?: number - 服务评分（可选，0-100）
     *   - rating_comment?: string - 评价内容（可选）
     *   - rating_photos?: string[] - 评价图片路径数组（可选）
     * 
     * @returns Promise<SupplierRating> 创建的评分记录对象
     * 
     * 数据流向：
     * 1. 用户在订单详情页点击"评分"按钮
     * 2. 填写评分表单（质量评分必填，交期和服务评分可选）
     * 3. 前端调用：purchaseApi.ratePurchaseOrder(123, { quality_score: 90, delivery_score: 85, ... })
     * 4. 发送请求：POST /api/v1/purchase/orders/123/rate
     * 5. 后端处理：PurchaseOrderController@rateOrder 方法
     *    - 验证数据（使用 RateOrderRequest）
     *    - 计算综合评分（自动计算）
     *    - 计算星级评分（根据综合评分自动计算，1-5星）
     *    - 创建 SupplierRating 记录
     *    - 更新订单的 is_rated 标记
     *    - 更新供应商的质量评分（重新计算平均值）
     * 6. 返回数据：新创建的 SupplierRating 对象
     * 
     * 代码示例：
     * ```typescript
     * // 对订单进行评分
     * const rating = await purchaseApi.ratePurchaseOrder(123, {
     *   quality_score: 90,      // 质量评分：90分
     *   delivery_score: 85,     // 交期评分：85分
     *   service_score: 88,      // 服务评分：88分
     *   rating_comment: '质量很好，交期准时',
     *   rating_photos: ['path/to/photo1.jpg', 'path/to/photo2.jpg']
     * })
     * 
     * console.log(rating.overallScore)  // 综合评分：自动计算的平均分
     * console.log(rating.starRating)   // 星级评分：自动计算的星级（1-5星）
     * ```
     * 
     * 错误处理：
     * - 404：订单不存在
     * - 422：数据验证失败（质量评分不在0-100范围内等）
     * - 409：订单已评分（每个订单只能评分一次）
     * - 403：无权限
     */
    ratePurchaseOrder(id: number, data: RateOrderForm) {
      return client.post<SupplierRating>(
        API_ROUTES.purchase.rate(id),  // '/api/v1/purchase/orders/123/rate'
        data
      )
    },
    
    /**
     * 获取订单明细列表
     * 
     * 使用场景：
     * - 在订单详情页显示订单明细
     * - 查看订单的所有物料行项
     * 
     * @param id - 订单 ID
     * 
     * @returns Promise<PurchaseOrderItem[]> 订单明细数组
     */
    getPurchaseOrderItems(id: number) {
      return client.get<PurchaseOrderItem[]>(
        API_ROUTES.purchase.ordersItems(id)  // '/api/v1/purchase/orders/123/items'
      )
    },
    
    /**
     * 获取订单收货记录列表
     * 
     * 使用场景：
     * - 在订单详情页显示收货记录
     * - 查看订单的所有收货历史
     * 
     * @param id - 订单 ID
     * 
     * @returns Promise<PurchaseReceiving[]> 收货记录数组（按收货时间降序）
     */
    getPurchaseOrderReceivings(id: number) {
      return client.get<PurchaseReceiving[]>(
        API_ROUTES.purchase.ordersReceivings(id)  // '/api/v1/purchase/orders/123/receivings'
      )
    },
    
    /**
     * 获取订单评分记录
     * 
     * 使用场景：
     * - 在订单详情页显示评分记录
     * - 查看订单的评分信息（如果有）
     * 
     * @param id - 订单 ID
     * 
     * @returns Promise<SupplierRating | null> 评分记录（如果已评分）或 null（未评分）
     */
    getPurchaseOrderRating(id: number) {
      return client.get<SupplierRating | null>(
        API_ROUTES.purchase.ordersRatings(id)  // '/api/v1/purchase/orders/123/ratings'
      )
    },
    
    /**
     * 导出订单列表
     * 
     * 使用场景：
     * - 导出符合条件的订单数据为 Excel 文件
     * 
     * @param params - 查询参数（与 getPurchaseOrderList 相同）
     * 
     * @returns Promise<Blob> 文件数据（二进制）
     */
    exportPurchaseOrders(params?: PurchaseOrderQueryParams) {
      return client.post<Blob>(
        API_ROUTES.purchase.ordersExport,  // '/api/v1/purchase/orders/export'
        params,
        { responseType: 'blob' }
      )
    },
    
    /**
     * 导入订单列表
     * 
     * 使用场景：
     * - 批量导入订单数据
     * 
     * @param file - Excel 文件对象
     * 
     * @returns Promise<BatchImportResult> 导入结果
     */
    importPurchaseOrders(file: File | FormData) {
      const formData = file instanceof FormData ? file : (() => {
        const fd = new FormData()
        fd.append('file', file)
        return fd
      })()
      
      return client.post<{ successCount: number; failureCount: number; errors?: any[] }>(
        API_ROUTES.purchase.ordersImport,  // '/api/v1/purchase/orders/import'
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )
    },
    
    // ============================================================================
    // ========== 收货管理 API ==========
    // ============================================================================
    
    /**
     * 获取收货记录列表（分页）
     * 
     * 使用场景：
     * - 在收货管理页面的主列表中使用
     * - 支持分页、筛选功能（按订单、供应商、物料、质检状态、日期范围等）
     * 
     * @param params - 查询参数对象（可选）
     *   - page?: number - 页码
     *   - per_page?: number - 每页数量
     *   - purchase_order_id?: number - 采购订单ID筛选
     *   - supplier_id?: number - 供应商ID筛选
     *   - material_id?: number - 物料ID筛选
     *   - quality_status?: QualityStatus - 质检状态筛选
     *     可选值：1-待检, 2-合格, 3-不合格
     *   - received_at_start?: string - 收货日期开始（YYYY-MM-DD）
     *   - received_at_end?: string - 收货日期结束（YYYY-MM-DD）
     *   - today_only?: boolean - 仅今日收货（true-仅今日）
     *   - sort_by?: string - 排序字段
     *   - sort_order?: 'asc' | 'desc' - 排序方向
     * 
     * @returns Promise<PaginationResult<PurchaseReceiving>> 分页结果对象
     * 
     * 代码示例：
     * ```typescript
     * // 获取收货记录列表
     * // ⚠️ 注意：routes.ts 中可能缺少此路径，需要重新导出或手动添加
     * const receivings = await purchaseApi.getReceivingList({ page: 1 })
     * 
     * // 筛选今日收货
     * const todayReceivings = await purchaseApi.getReceivingList({
     *   today_only: true
     * })
     * 
     * // 筛选特定订单的收货记录
     * const orderReceivings = await purchaseApi.getReceivingList({
     *   purchase_order_id: 123
     * })
     * ```
     */
    getReceivingList(params?: ReceivingQueryParams) {
      return client.get<PaginationResult<PurchaseReceiving>>(
        API_ROUTES.purchase.receivings,  // '/api/v1/purchase/receivings'
        { params }
      )
    },
    
    /**
     * 获取收货详情
     * 
     * 使用场景：
     * - 在收货详情页面显示完整信息
     * - 查看收货记录的详细资料（包含订单、物料、仓库、库存批次等关联信息）
     * 
     * @param id - 收货记录的唯一标识符（主键）
     * 
     * @returns Promise<PurchaseReceiving> 单个收货记录对象（包含所有关联信息）
     */
    getReceivingDetail(id: number) {
      return client.get<PurchaseReceiving>(
        API_ROUTES.purchase.purchaseReceivings(id)  // '/api/v1/purchase/receivings/123'
      )
    },
    
    /**
     * 收货操作（完整流程）
     * 
     * 使用场景：
     * - 在收货页面（移动端为主）完成收货操作
     * - 扫码收货、拍照上传、创建库存批次、生成入库事务等
     * 
     * @param data - 收货数据
     *   字段要求（参考 ReceiveMaterialForm 类型）：
     *   - purchase_order_id: number - 采购订单ID（必填）
     *   - order_item_id: number - 订单明细ID（必填）
     *   - supplier_batch_no?: string - 供应商批次号（可选）
     *   - received_quantity: number - 收货数量（必填）
     *   - is_over_received?: 0 | 1 - 是否超收（可选，0-否, 1-是）
     *   - warehouse_id: number - 仓库ID（必填）
     *   - location_id?: number - 库位ID（可选）
     *   - receiving_photos?: string[] - 收货照片路径数组（可选）
     *   - remark?: string - 备注（可选）
     * 
     * @returns Promise<PurchaseReceiving> 创建的收货记录对象
     * 
     * 数据流向：
     * 1. 用户在移动端扫描订单号或物料条码
     * 2. 填写收货数量、选择仓库库位、拍照上传
     * 3. 前端调用：purchaseApi.receiveMaterial({ purchase_order_id: 123, order_item_id: 456, received_quantity: 100, ... })
     * 4. 发送请求：POST /api/v1/purchase/receivings（或通过订单的收货接口）
     * 5. 后端处理：ReceivingController@receive 方法
     *    - 验证数据（使用 ReceiveMaterialRequest）
     *    - 自动生成收货单号（如：REC-20250104-000001）
     *    - 检查是否超收（如果数量超过订单数量且未标记超收，则拒绝）
     *    - 创建收货记录（质量状态默认为"待检"）
     *    - 创建库存批次（自动生成内部批次号）
     *    - 生成入库事务记录
     *    - 更新订单的已收货数量
     *    - 更新订单状态（部分收货或已完成）
     * 6. 返回数据：新创建的 PurchaseReceiving 对象（包含库存批次信息）
     * 
     * 代码示例：
     * ```typescript
     * // 收货操作
     * const receiving = await purchaseApi.receiveMaterial({
     *   purchase_order_id: 123,
     *   order_item_id: 456,
     *   received_quantity: 100,
     *   supplier_batch_no: 'BATCH001',
     *   warehouse_id: 1,
     *   location_id: 10,
     *   receiving_photos: ['path/to/photo1.jpg'],
     *   remark: '正常收货'
     * })
     * 
     * console.log(receiving.receivingNo)      // 收货单号
     * console.log(receiving.internalBatchNo) // 内部批次号
     * ```
     * 
     * 错误处理：
     * - 404：订单或订单明细不存在
     * - 422：数据验证失败或超收未标记
     * - 403：无权限
     * - 500：服务器错误（库存创建失败等）
     */
    receiveMaterial(data: ReceiveMaterialForm) {
      // 收货通过收货管理接口：POST /api/v1/purchase/receivings
      // data 中需要包含 purchase_order_id
      return client.post<PurchaseReceiving>(
        API_ROUTES.purchase.receivings,  // '/api/v1/purchase/receivings'
        data
      )
    },
    
    /**
     * 上传收货照片
     * 
     * 使用场景：
     * - 在收货时上传收货照片
     * - 支持多张照片上传
     * 
     * @param file - 照片文件对象
     * @param data - 附加数据
     *   - order_no: string - 订单号（必填）
     *   - line_no: number - 订单行号（必填）
     * 
     * @returns Promise<{ path: string; url: string }> 上传后的文件路径和URL
     * 
     * 代码示例：
     * ```typescript
     * // 上传收货照片
     * const formData = new FormData()
     * formData.append('photo', file)
     * formData.append('order_no', 'PO-20250104-000001')
     * formData.append('line_no', '1')
     * 
     * const result = await purchaseApi.uploadReceivingPhoto(formData)
     * console.log(result.path)  // 文件路径
     * console.log(result.url)    // 文件URL
     * ```
     */
    uploadReceivingPhoto(file: File | FormData, data?: { order_no: string; line_no: number }) {
      const formData = file instanceof FormData ? file : (() => {
        const fd = new FormData()
        fd.append('photo', file)
        if (data) {
          fd.append('order_no', data.order_no)
          fd.append('line_no', String(data.line_no))
        }
        return fd
      })()
      
      return client.post<{ path: string; url: string }>(
        API_ROUTES.purchase.uploadPhoto,  // '/api/v1/purchase/receivings/upload-photo'
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )
    },
    
    // ============================================================================
    // ========== 采购看板 API ==========
    // ============================================================================
    
    /**
     * 获取采购看板数据
     * 
     * 使用场景：
     * - 在采购看板页面显示关键指标和数据可视化
     * - 展示6张统计卡片和4个图表数据
     * 
     * @returns Promise<PurchaseDashboardData> 看板数据对象
     *   返回的数据结构：
     *   {
     *     // 统计卡片数据（6张卡片）
     *     cards: {
     *       totalOrders: number,        // 总订单数
     *       pendingOrders: number,      // 待审核订单数
     *       overdueOrders: number,       // 逾期订单数
     *       totalAmount: number,        // 订单总金额
     *       receivedAmount: number,     // 已收货金额
     *       pendingAmount: number,      // 待收货金额
     *     },
     *     // 图表数据（4个图表）
     *     charts: {
     *       orderStatusChart: { ... },      // 订单状态分布图
     *       orderTrendChart: { ... },       // 订单趋势图
     *       supplierRankingChart: { ... },  // 供应商排名图
     *       materialRankingChart: { ... },  // 物料排名图
     *     }
     *   }
     * 
     * 数据流向：
     * 1. 用户打开采购看板页面
     * 2. 前端调用：purchaseApi.getDashboardData()
     * 3. 发送请求：GET /api/v1/purchase/dashboard
     * 4. 后端处理：DashboardController@index 方法
     *    - 统计各类数据（订单数、金额、逾期数等）
     *    - 计算图表数据（状态分布、趋势、排名等）
     *    - 返回完整的看板数据
     * 5. 前端显示：使用数据渲染统计卡片和图表
     * 
     * 代码示例：
     * ```typescript
     * // 获取看板数据
     * const dashboard = await purchaseApi.getDashboardData()
     * console.log(dashboard.cards.totalOrders)  // 总订单数
     * console.log(dashboard.cards.overdueOrders) // 逾期订单数
     * 
     * // 渲染图表
     * const orderStatusData = dashboard.charts.orderStatusChart
     * // 使用图表库渲染订单状态分布图
     * ```
     */
    getDashboardData() {
      return client.get<Record<string, any>>(
        API_ROUTES.purchase.dashboard  // '/api/v1/purchase/dashboard'
      )
    },

    // ============================================================================
    // ========== 供应商索赔管理 API ==========
    // ============================================================================

    /**
     * 获取索赔类型选项
     * 
     * @returns Promise<Array<{ value: number; label: string }>> 索赔类型选项列表
     */
    getClaimTypeOptions() {
      return client.get<Array<{ value: number; label: string }>>(
        API_ROUTES.purchase.claimsTypeOptions
      )
    },

    /**
     * 获取索赔状态选项
     * 
     * @returns Promise<Array<{ value: number; label: string }>> 索赔状态选项列表
     */
    getClaimStatusOptions() {
      return client.get<Array<{ value: number; label: string }>>(
        API_ROUTES.purchase.claimsStatusOptions
      )
    },

    /**
     * 获取索赔列表（分页）
     * 
     * @param params - 查询参数
     * @returns Promise<PaginationResult<SupplierClaim>> 索赔列表分页结果
     */
    getClaimList(params?: SupplierClaimListParams) {
      return client.get<PaginationResult<SupplierClaim>>(
        API_ROUTES.purchase.claims,
        { params }
      )
    },

    /**
     * 创建索赔
     * 
     * @param data - 创建索赔的表单数据
     * @returns Promise<SupplierClaim> 创建的索赔记录
     */
    createClaim(data: CreateSupplierClaimParams) {
      // 转换为 snake_case 格式
      const payload: Record<string, unknown> = {
        supplier_id: data.supplierId,
        claim_type: data.claimType,
        claim_reason: data.claimReason,
        claim_amount: data.claimAmount,
      }
      if (data.purchaseOrderId !== undefined) payload.purchase_order_id = data.purchaseOrderId
      if (data.receivingId !== undefined) payload.receiving_id = data.receivingId
      if (data.claimQuantity !== undefined) payload.claim_quantity = data.claimQuantity
      if (data.attachments !== undefined) payload.attachments = data.attachments
      if (data.remark !== undefined) payload.remark = data.remark

      return client.post<SupplierClaim>(
        API_ROUTES.purchase.claims,
        payload
      )
    },

    /**
     * 获取索赔详情
     * 
     * @param id - 索赔ID
     * @returns Promise<SupplierClaim> 索赔详情
     */
    getClaimDetail(id: number) {
      return client.get<SupplierClaim>(
        API_ROUTES.purchase.purchaseClaims(id)
      )
    },

    /**
     * 更新索赔
     * 
     * @param id - 索赔ID
     * @param data - 更新数据
     * @returns Promise<SupplierClaim> 更新后的索赔记录
     */
    updateClaim(id: number, data: UpdateSupplierClaimParams) {
      // 转换为 snake_case 格式
      const payload: Record<string, unknown> = {}
      if (data.purchaseOrderId !== undefined) payload.purchase_order_id = data.purchaseOrderId
      if (data.receivingId !== undefined) payload.receiving_id = data.receivingId
      if (data.claimType !== undefined) payload.claim_type = data.claimType
      if (data.claimReason !== undefined) payload.claim_reason = data.claimReason
      if (data.claimAmount !== undefined) payload.claim_amount = data.claimAmount
      if (data.claimQuantity !== undefined) payload.claim_quantity = data.claimQuantity
      if (data.attachments !== undefined) payload.attachments = data.attachments
      if (data.remark !== undefined) payload.remark = data.remark

      return client.put<SupplierClaim>(
        API_ROUTES.purchase.purchaseClaims(id),
        payload
      )
    },

    /**
     * 删除索赔
     * 
     * @param id - 索赔ID
     * @returns Promise<void>
     */
    deleteClaim(id: number) {
      return client.delete<void>(
        API_ROUTES.purchase.purchaseClaims(id)
      )
    },

    /**
     * 确认索赔
     * 
     * @param id - 索赔ID
     * @param data - 确认数据（可包含实际赔付金额和备注）
     * @returns Promise<SupplierClaim> 更新后的索赔记录
     */
    confirmClaim(id: number, data?: SupplierClaimActionParams) {
      // 转换为 snake_case 格式
      const payload: Record<string, unknown> = {}
      if (data?.actualAmount !== undefined) payload.actual_amount = data.actualAmount
      if (data?.remark !== undefined) payload.remark = data.remark

      return client.post<SupplierClaim>(
        API_ROUTES.purchase.claimsConfirm(id),
        payload
      )
    },

    /**
     * 完成收款（财务确认供应商赔款已到账）
     * 
     * @param id - 索赔ID
     * @param data - 收款数据（包含实际收款金额、收款方式和备注）
     * @returns Promise<SupplierClaim> 更新后的索赔记录
     */
    payClaim(id: number, data?: SupplierClaimActionParams & { paymentMethod?: string }) {
      // 转换为 snake_case 格式
      const payload: Record<string, unknown> = {}
      if (data?.actualAmount !== undefined) payload.actual_amount = data.actualAmount
      if (data?.paymentMethod !== undefined) payload.payment_method = data.paymentMethod
      if (data?.remark !== undefined) payload.remark = data.remark

      return client.post<SupplierClaim>(
        API_ROUTES.purchase.pay(id),
        payload
      )
    },

    /**
     * 关闭索赔
     * 
     * @param id - 索赔ID
     * @param remark - 关闭备注
     * @returns Promise<SupplierClaim> 更新后的索赔记录
     */
    closeClaim(id: number, remark?: string) {
      return client.post<SupplierClaim>(
        API_ROUTES.purchase.claimsClose(id),
        { remark }
      )
    },

    /**
     * 驳回索赔
     * 
     * @param id - 索赔ID
     * @param remark - 驳回原因
     * @returns Promise<SupplierClaim> 更新后的索赔记录
     */
    rejectClaim(id: number, remark?: string) {
      return client.post<SupplierClaim>(
        API_ROUTES.purchase.rejectApplication(id),
        { remark }
      )
    },

    /**
     * 获取供应商的索赔列表
     * 
     * @param supplierId - 供应商ID
     * @param params - 查询参数
     * @returns Promise<PaginationResult<SupplierClaim>> 索赔列表分页结果
     */
    getSupplierClaims(supplierId: number, params?: Omit<SupplierClaimListParams, 'supplierId'>) {
      return client.get<PaginationResult<SupplierClaim>>(
        API_ROUTES.purchase.suppliersClaims(supplierId),
        { params }
      )
    },

    // ============================================================================
    // ========== 供应商绩效评估 API ==========
    // ============================================================================

    /**
     * 获取绩效看板数据
     * 
     * @returns Promise<PerformanceDashboard> 绩效看板数据
     */
    getPerformanceDashboard() {
      return client.get<PerformanceDashboard>(
        API_ROUTES.purchase.performanceDashboard
      )
    },

    /**
     * 获取绩效TOP供应商
     * 
     * @param limit - 数量限制，默认10
     * @returns Promise<TopSupplier[]> TOP供应商列表
     */
    getTopSuppliers(limit: number = 10) {
      return client.get<TopSupplier[]>(
        API_ROUTES.purchase.performanceTopSuppliers,
        { params: { limit } }
      )
    },

    /**
     * 获取预警供应商
     * 
     * @returns Promise<AlertSupplier[]> 预警供应商列表
     */
    getAlertSuppliers() {
      return client.get<AlertSupplier[]>(
        API_ROUTES.purchase.performanceAlertSuppliers
      )
    },

    /**
     * 获取月度绩效趋势
     * 
     * @param months - 月数，默认12
     * @returns Promise<MonthlyTrendItem[]> 月度趋势数据
     */
    getMonthlyTrend(months: number = 12) {
      return client.get<MonthlyTrendItem[]>(
        API_ROUTES.purchase.performanceMonthlyTrend,
        { params: { months } }
      )
    },

    /**
     * 获取供应商绩效详情
     * 
     * @param supplierId - 供应商ID
     * @returns Promise<SupplierPerformanceDetail> 供应商绩效详情
     */
    getSupplierPerformance(supplierId: number) {
      return client.get<SupplierPerformanceDetail>(
        API_ROUTES.purchase.suppliersPerformance(supplierId)
      )
    },

    /**
     * 计算供应商绩效
     * 
     * @param supplierId - 供应商ID
     * @param months - 统计月数，默认6
     * @returns Promise<SupplierPerformance> 计算后的绩效数据
     */
    calculateSupplierPerformance(supplierId: number, months: number = 6) {
      return client.get<SupplierPerformance>(
        API_ROUTES.purchase.performanceCalculate(supplierId),
        { params: { months } }
      )
    },

    /**
     * 更新供应商绩效等级
     * 
     * @param supplierId - 供应商ID
     * @param force - 是否强制更新（忽略手动设置标记）
     * @returns Promise<UpdateLevelResult> 更新结果
     */
    updateSupplierLevel(supplierId: number, force: boolean = false) {
      return client.post<UpdateLevelResult>(
        API_ROUTES.purchase.updateLevel(supplierId),
        { force }
      )
    },

    /**
     * 批量更新所有供应商绩效等级
     * 
     * @param force - 是否强制更新
     * @returns Promise<BatchUpdateLevelsResult> 批量更新结果
     */
    batchUpdateLevels(force: boolean = false) {
      return client.post<BatchUpdateLevelsResult>(
        API_ROUTES.purchase.batchUpdateLevels,
        { force }
      )
    },

    // ============================================================================
    // ========== 采购退货管理 API ==========
    // ============================================================================

    /**
     * 获取退货类型选项
     * 
     * @returns Promise<Array<{ value: string; label: string }>> 退货类型选项列表
     */
    getReturnTypeOptions() {
      return client.get<Array<{ value: string; label: string }>>(
        API_ROUTES.purchase.returnsTypeOptions  // '/api/v1/purchase/returns/type-options'
      )
    },

    /**
     * 获取退货状态选项
     * 
     * @returns Promise<Array<{ value: number; label: string }>> 退货状态选项列表
     */
    getReturnStatusOptions() {
      return client.get<Array<{ value: number; label: string }>>(
        API_ROUTES.purchase.returnsStatusOptions  // '/api/v1/purchase/returns/status-options'
      )
    },

    /**
     * 从收货记录创建退货单（APP端使用）
     * 
     * 使用场景：
     * - 在 APP 端收货详情页，对已完成收货的物料发起退货
     * - 系统会自动关联收货单信息（供应商、物料、批次等）
     * 
     * @param data - 退货数据
     *   字段要求（参考 CreateReturnFromReceivingForm 类型）：
     *   - receiving_id: number - 收货记录ID（必填）
     *   - return_type: string - 退货类型（必填）：QUALITY_ISSUE/WRONG_DELIVERY/OVER_DELIVERY/DAMAGED/OTHER
     *   - quantity: number - 退货数量（必填，>0，不能超过收货数量）
     *   - return_reason: string - 退货原因（必填）
     *   - auto_submit?: boolean - 是否自动提交审批（可选）
     *   - remark?: string - 备注（可选）
     * 
     * @returns Promise<PurchaseReturn> 创建的退货单对象
     * 
     * 数据流向：
     * 1. 用户在 APP 端收货详情页点击已完成物料的"退货"按钮
     * 2. 填写退货数量、选择退货类型、输入退货原因
     * 3. 前端调用：purchaseApi.createReturnFromReceiving({ receiving_id: 123, return_type: 'QUALITY_ISSUE', quantity: 10, return_reason: '质量不合格' })
     * 4. 发送请求：POST /api/v1/purchase/returns/from-receiving
     * 5. 后端处理：PurchaseReturnController@createFromReceiving 方法
     *    - 验证数据
     *    - 根据 receiving_id 自动填充供应商、物料、批次信息
     *    - 自动生成退货单号（如：RTN20251203000001）
     *    - 创建退货单记录
     *    - 扣减库存批次数量
     *    - 如果 auto_submit=true，自动提交审批
     * 6. 返回数据：新创建的 PurchaseReturn 对象
     */
    createReturnFromReceiving(data: CreateReturnFromReceivingForm) {
      return client.post<PurchaseReturn>(
        API_ROUTES.purchase.returnsFromReceiving,  // '/api/v1/purchase/returns/from-receiving'
        data
      )
    },

    /**
     * 获取退货单列表（分页）
     * 
     * @param params - 查询参数
     * @returns Promise<PaginationResult<PurchaseReturn>> 退货单分页列表
     */
    getReturnList(params?: { page?: number; per_page?: number; status?: number }) {
      return client.get<PaginationResult<PurchaseReturn>>(
        API_ROUTES.purchase.returns,  // '/api/v1/purchase/returns'
        { params }
      )
    },

    /**
     * 获取退货单详情
     * 
     * @param id - 退货单ID
     * @returns Promise<PurchaseReturn> 退货单详情
     */
    getReturnDetail(id: number) {
      return client.get<PurchaseReturn>(
        API_ROUTES.purchase.purchaseReturns(id)  // '/api/v1/purchase/returns/{id}'
      )
    },

    /**
     * 提交退货单（草稿->待审批）
     * 
     * @param id - 退货单ID
     * @returns Promise<PurchaseReturn> 更新后的退货单
     */
    submitReturn(id: number) {
      return client.post<PurchaseReturn>(
        API_ROUTES.purchase.returnsSubmit(id)  // '/api/v1/purchase/returns/{id}/submit'
      )
    },

    /**
     * 审批退货单（待审批->已审批/已驳回）
     * 
     * @param id - 退货单ID
     * @param approved - 是否通过（true=通过，false=驳回）
     * @param remark - 审批备注（可选）
     * @returns Promise<PurchaseReturn> 更新后的退货单
     */
    approveReturn(id: number, approved: boolean = true, remark?: string) {
      return client.post<PurchaseReturn>(
        API_ROUTES.purchase.returnsApprove(id),  // '/api/v1/purchase/returns/{id}/approve'
        { approved, remark }
      )
    },

    /**
     * 标记退货单已发货（已审批->退货中）
     * 
     * @param id - 退货单ID
     * @returns Promise<PurchaseReturn> 更新后的退货单
     */
    shipReturn(id: number) {
      return client.post<PurchaseReturn>(
        API_ROUTES.purchase.returnsShip(id)  // '/api/v1/purchase/returns/{id}/ship'
      )
    },

    /**
     * 完成退货（退货中->已完成）
     * 
     * @param id - 退货单ID
     * @returns Promise<PurchaseReturn> 更新后的退货单
     */
    completeReturn(id: number) {
      return client.post<PurchaseReturn>(
        API_ROUTES.purchase.returnsComplete(id)  // '/api/v1/purchase/returns/{id}/complete'
      )
    },

    /**
     * 取消/驳回退货单
     * 
     * @param id - 退货单ID
     * @returns Promise<PurchaseReturn> 更新后的退货单
     */
    cancelReturn(id: number) {
      return client.post<PurchaseReturn>(
        API_ROUTES.purchase.returnsCancel(id)  // '/api/v1/purchase/returns/{id}/cancel'
      )
    },
  }
}
